import React, { useState, useRef, useEffect } from 'react'
import { BellIcon } from '@bizfirst/common-react'
import type { NotificationItem } from './AuthHeaderControls'

export interface NotificationsButtonProps {
  items: NotificationItem[]
  unreadCount: number
  onMarkAllRead?: () => void
  viewAllUrl?: string
}

/**
 * `NotificationsButton` — presentational only (design doc §4.5). Renders whatever
 * `items`/`unreadCount` it's given and calls the callbacks it's given; it does not
 * fetch, poll, or own a polling interval. The consuming app (or, eventually,
 * `@bizfirst/notifications-react` — a separate, still-in-progress package, not a
 * dependency of this one) is responsible for supplying live data.
 *
 * Works across all viewport sizes (desktop, tablet, mobile).
 */
export function NotificationsButton({
  items,
  unreadCount,
  onMarkAllRead,
  viewAllUrl,
}: NotificationsButtonProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && panelRef.current) {
        if (!buttonRef.current.contains(event.target as Node) && !panelRef.current.contains(event.target as Node)) {
          setIsOpen(false)
        }
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
    return undefined
  }, [isOpen])

  const recentItems = items.slice(0, 8)

  return (
    <div className="auth-notifications">
      <button
        ref={buttonRef}
        className="auth-notifications-button"
        onClick={() => setIsOpen((open) => !open)}
        title="Notifications"
        aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
        aria-expanded={isOpen}
      >
        <BellIcon size={20} />
        {unreadCount > 0 && (
          <span className="auth-notifications-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div ref={panelRef} className="auth-notifications-panel">
          <div className="auth-notifications-header">
            <span>Notifications</span>
            {onMarkAllRead && unreadCount > 0 && (
              <button type="button" className="auth-notifications-mark-read" onClick={onMarkAllRead}>
                Mark all read
              </button>
            )}
          </div>

          {recentItems.length === 0 ? (
            <div className="auth-notifications-empty">No notifications</div>
          ) : (
            <ul className="auth-notifications-list">
              {recentItems.map((item) => {
                const content = (
                  <>
                    <div className="auth-notifications-item-title">
                      {!item.read && <span className="auth-notifications-item-dot" />}
                      {item.title}
                    </div>
                    {item.body && <div className="auth-notifications-item-body">{item.body}</div>}
                    <div className="auth-notifications-item-time">{item.timestamp}</div>
                  </>
                )
                return (
                  <li key={item.id} className={`auth-notifications-item${item.read ? '' : ' unread'}`}>
                    {item.href ? (
                      <a href={item.href} onClick={item.onClick}>
                        {content}
                      </a>
                    ) : (
                      <button type="button" onClick={item.onClick}>
                        {content}
                      </button>
                    )}
                  </li>
                )
              })}
            </ul>
          )}

          {viewAllUrl && (
            <a href={viewAllUrl} className="auth-notifications-view-all">
              View all
            </a>
          )}
        </div>
      )}

      <style>{`
        .auth-notifications {
          position: relative;
          display: flex;
          align-items: center;
        }

        .auth-notifications-button {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          padding: 0;
          background: var(--bg-raised);
          border: 1px solid var(--border-default); box-sizing: border-box;
          border-radius: 6px;
          color: inherit;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .auth-notifications-button:hover {
          background: var(--border-default);
        }

        .auth-notifications-badge {
          position: absolute;
          top: 2px;
          right: 2px;
          min-width: 16px;
          height: 16px;
          padding: 0 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--error);
          color: var(--text-on-primary);
          font-size: 10px;
          font-weight: 700;
          line-height: 1;
          border-radius: 999px;
        }

        .auth-notifications-panel {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          width: 320px;
          max-width: calc(100vw - 32px);
          background: var(--color-bg-secondary, var(--bg-secondary, #1c1f26));
          border: 1px solid var(--color-border, var(--border, #30363d));
          border-radius: 8px;
          box-shadow: 0 10px 25px rgb(var(--black-rgb) / 0.4);
          z-index: 1000;
          overflow: hidden;
        }

        /* Phones: this panel is 320px and anchored with right: 0 to a button that sits ~110px
           from the right edge, so it started at x=-48 on a 382px screen and its left side --
           including the "Notifications" heading -- fell outside the viewport. max-width alone
           cannot fix that, because the overflow comes from the anchor rather than the width.
           Anchor it to the VIEWPORT's right edge rather than the button, keeping its designed
           320px width -- stretching it edge-to-edge instead would make a two-line menu read as a
           full-width banner. right: 10px matches the header's own horizontal padding at these
           widths, so the panel lines up with the button row above it; top: 70px is the shared
           mobile app bar height (bizfirst-common-themes' app-shell.css). */
        @media (max-width: 480px) {
          .auth-notifications-panel {
            position: fixed;
            top: 70px;
            left: auto;
            width: 320px;
          }
        }

        /* The right offset tracks .app-header's own horizontal padding per breakpoint (8 / 10 /
           12px in app-shell.css), so the panel's right edge lands exactly on the button row above
           it at every phone width. A single flat value left it 2px proud of the buttons between
           425 and 480px, where the header switches to 12px. */
        @media (max-width: 374px) {
          .auth-notifications-panel {
            right: 8px;
            max-width: calc(100vw - 16px);
          }
        }

        @media (min-width: 375px) and (max-width: 424px) {
          .auth-notifications-panel {
            right: 10px;
            max-width: calc(100vw - 20px);
          }
        }

        @media (min-width: 425px) and (max-width: 480px) {
          .auth-notifications-panel {
            right: 12px;
            max-width: calc(100vw - 24px);
          }
        }

        .auth-notifications-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          font-size: 14px;
          font-weight: 600;
          color: var(--color-text-primary, var(--text-primary, #e6edf3));
          border-bottom: 1px solid var(--color-border, var(--border, #30363d));
        }

        .auth-notifications-mark-read {
          background: none;
          border: none;
          padding: 0;
          font-size: 12px;
          font-weight: 500;
          color: var(--color-primary, var(--obs-primary, #309B48));
          cursor: pointer;
        }

        .auth-notifications-empty {
          padding: 24px 16px;
          text-align: center;
          font-size: 13px;
          color: var(--color-text-secondary, var(--text-secondary, #6B7879));
        }

        .auth-notifications-list {
          list-style: none;
          margin: 0;
          padding: 0;
          max-height: 360px;
          overflow-y: auto;
        }

        .auth-notifications-item {
          border-bottom: 1px solid var(--border-default);
        }

        .auth-notifications-item:last-child {
          border-bottom: none;
        }

        .auth-notifications-item > a,
        .auth-notifications-item > button {
          display: block;
          width: 100%;
          padding: 10px 16px;
          background: none;
          border: none;
          text-align: left;
          text-decoration: none;
          color: inherit;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .auth-notifications-item.unread > a,
        .auth-notifications-item.unread > button {
          background: rgb(var(--primary-rgb) / 0.06);
        }

        .auth-notifications-item > a:hover,
        .auth-notifications-item > button:hover {
          background: var(--color-bg-tertiary, var(--bg-tertiary, #30363d));
        }

        .auth-notifications-item-title {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 600;
          color: var(--color-text-primary, var(--text-primary, #e6edf3));
        }

        .auth-notifications-item-dot {
          flex-shrink: 0;
          width: 6px;
          height: 6px;
          border-radius: 999px;
          background: var(--color-primary, var(--obs-primary, #309B48));
        }

        .auth-notifications-item-body {
          margin-top: 2px;
          font-size: 12px;
          color: var(--color-text-secondary, var(--text-secondary, #6B7879));
        }

        .auth-notifications-item-time {
          margin-top: 4px;
          font-size: 11px;
          color: var(--color-text-secondary, var(--text-secondary, #6B7879));
          opacity: 0.8;
        }

        .auth-notifications-view-all {
          display: block;
          padding: 10px 16px;
          text-align: center;
          font-size: 13px;
          font-weight: 600;
          color: var(--color-primary, var(--obs-primary, #309B48));
          text-decoration: none;
          border-top: 1px solid var(--color-border, var(--border, #30363d));
        }

        .auth-notifications-view-all:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  )
}

export default NotificationsButton
